<template>
  <div ref="VIREMENT_ETABLISSEMENT">
    <section-header
        v-if="!hideTitle"
        ref="CI_TRANSACTION_LIST"
        :ressource="ressource"
        :is-extended="extended"
        :dialog="dialog"
        :fiche=fiche
        @extended=setExtended
        @close-dialog=closeSection>
    </section-header>

    <v-row class="py-10 m-0">
      <v-col>
        <v-combobox
            ref="COMBO_BOX"
            v-model=etablissementSelected
            class="'edit-field'"
            :items="etablissementList"
            :label=labelEtablissement
            :placeholder="placeholder"
            :persistent-placeholder="placeholder!=''"
            :outlined="false"
            :ripple="false"
            dense>
        </v-combobox>
      </v-col>
      <v-col>
        <div class="d-flex flex-row-reverse">
          <loader-button
              class="ms-2"
              label="Exporter"
              :loading="exportLoading"
              :disabled="!exportDisabled"
              @callback="exportTransactions">
          </loader-button>
          <loader-button
              class='ms-2'
              type='button'
              :label='generateLabel'
              :loading="generateLoading"
              :disabled='!generateDisabled'
              @callback="generate">
          </loader-button>
          <loader-button
              class="ms-2"
              label="Recherche"
              :loading="searchLoading"
              :disabled="searchDisabled"
              @callback="search">
          </loader-button>
        </div>
      </v-col>
    </v-row>

    <div v-if="extended">
      <v-data-table
          v-model="selected"
          :height="listHeight"
          dense fixed-header
          :headers="headers"
          :items="items"
          :items-per-page="-1"
          :must-sort="true"
          :mobile-breakpoint="100"
          item-key="transactionId"
          class="white"
          no-data-text="Aucune donnée"
          :disable-pagination="true"
          :hide-default-footer="true"
          :show-select="true"
          return-object
          @toggle-select-all="selectAll">
        <template #[`item.data-table-select`]="props">
          <v-checkbox
              :model-value="isSelected(props.item)"
              :readonly="getDisabled(props.item)"
              :disabled="getDisabled(props.item)"
              @change="(event: any) => handleSelect(event.target.checked, props.item)"
          ></v-checkbox>
        </template>
        <template #[`item.date`]="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template #[`item.datePayed`]="{ item }">
          {{ formatDate(item.datePayed) }}
        </template>
        <template #[`item.montant`]="{ item }">
          {{ formatCurrency(item.montant) }}
        </template>
      </v-data-table>
    </div>

    <v-row class="mt-5">
      <v-col>
        <span class="me-10">Total : {{ totalMontant }} ({{ totalCount }} remboursements)</span>
        <span>Sélectionnés : {{ selectMontant }} ({{ selectCount }} remboursements)</span>
      </v-col>
    </v-row>
  </div>
</template>
<script lang="ts">
import {GenSection, RessourceGenSection} from '@/generated/ressources/generatedRessources';
import {FwkButtons, RessourceFwkButton} from '@/ressources/globalRessources';
import {Sections} from '@/generated/classes/AppSchema';
import Etablissement from '@/generated/classes/Etablissement';
import ListData, {ListFilter} from '@/generated/classes/ListData';
import Item from '@/framework/base/classes/Item';
import {Extended} from '@/framework/base/classes/Schema';
import {DateFormat} from '@/framework/base/helpers/DateFormat';
import {getCurrency} from '@/framework/base/helpers/Localization';
import TransactionList, {TransactionEtablissement, TransactionEtablissementQuery} from '@/generated/classes/TransactionList';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import {TransactionTypes} from '@/generated/classes/Transaction';
import {ObjTypes} from '@/generated/classes/ObjTypes';
import {defineComponent} from "vue";

export default defineComponent({
  name: 'SectionVirementEtablissement',
  components: {
    SectionHeader,
    LoaderButton
  },
  props: {
    hideTitle: {
      type: Boolean,
      required: false,
    },
    extended: {
      type: Boolean,
      required: false,
    },
    dialog: {
      type: Boolean,
      required: false,
    },
    fiche: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchLoading: false,
      exportLoading: false,
      generateLoading: false,
      sectionName: Sections.VIREMENT_ETABLISSEMENT,
      placeholder: 'Sélectionner',
      listHeight: 'calc(100vh - 500px)',
      selectedItems: [] as TransactionEtablissement[],
      transactionList: [] as TransactionEtablissement[],
      etablissementId: new Item(-1, 'A rembourser'),
      etablissementList: [] as Item[],
      labelEtablissement: 'Etablissement',
      totalCount: 0,
      totalMontant: "0,00 €",
      selectCount: 0,
      selectMontant: "0,00 €",
    };
  },
  computed: {
    ressource(): any {
      return RessourceGenSection(GenSection.VIREMENT_ETABLISSEMENT);
    },
    generateLabel(): any {
      return RessourceFwkButton(FwkButtons.GENERER_VIREMENT).label;
    },
    headers(): any {
      return [
        {title: "Code", value: 'etablissementId', cellClass: '', sortable: true},
        {title: "Nom", value: 'nom', cellClass: '', sortable: true},
        {title: "Date", value: 'date', cellClass: '', sortable: true},
        {title: "Montant", value: 'montant', cellClass: '', sortable: true, align: 'right'},
        {title: "N° virement", value: 'deliveryId', cellClass: '', sortable: true, align: 'right'},
        {title: "Encaissement", value: 'datePayed', cellClass: '', sortable: true, align: 'center'}
      ];
    },
    items(): TransactionEtablissement[] {
      return this.transactionList;
    },
    selected: {
      get(): TransactionEtablissement[] {
        return this.selectedItems;
      },
      set(transactions: TransactionEtablissement[]) {
        const res: TransactionEtablissement[] = [];
        this.selectCount = 0;
        this.selectMontant = "0,00 €";
        let selectMontant = 0;
        transactions.forEach((transaction) => {
          if (!this.getDisabled(transaction)) {
            res.push(transaction);
            this.selectCount++;
            if (transaction.montant) {
              selectMontant += transaction.montant;
            }
          }
        });
        this.selectMontant = getCurrency(selectMontant);
        this.selectedItems = res;
      },
    },
    searchDisabled(): boolean {
      return false;
    },
    generateDisabled(): boolean {
      return this.selectedItems.length > 0;
    },
    exportDisabled(): boolean {
      return this.selectedItems.length > 0;
    },
    etablissementSelected: {
      get(): Item {
        return this.etablissementId;
      },
      set(item: Item) {
        this.etablissementId = item;
      },
    },
  },
  mounted() {
    Etablissement.list(new ListFilter()).then((listData: ListData) => {
      const res: Item[] = [];
      res.push(new Item(-1, 'A rembourser'));
      const etablissements = listData.reccords as Etablissement[];
      etablissements.forEach((etablissement) => {
        res.push(new Item(etablissement.id, etablissement.id.toString() + " - " + etablissement.nom));
      });
      this.etablissementList = res;
    });
    const query = new TransactionEtablissementQuery();
    query.etablissementId = -1;
    this.loadTable(query);
  },
  methods: {
    loadTable(query: TransactionEtablissementQuery): void {
      this.searchLoading = true;
      this.transactionList = [];
      this.selectedItems = [];
      this.totalCount = 0;
      this.totalMontant = "0,00 €";
      TransactionList.listTransactionsEtablissement(query).then((res) => {
        this.transactionList = res;
        this.totalCount = res.length;
        let totalMontant = 0;
        res.forEach((t) => {
          if (t.montant) {
            totalMontant += t.montant;
          }
        });
        this.totalMontant = getCurrency(totalMontant);
        this.searchLoading = false;
        this.selected = res;
      }).catch(() => {
        this.searchLoading = false;
      });
    },
    handleSelect(isSelected: boolean, item: TransactionEtablissement) {
      const selectedItems = this.selectedItems;
      if (isSelected && !selectedItems.includes(item)) {
        selectedItems.push(item);
        this.selected = selectedItems;
      } else {
        this.selected = this.selected.filter(selectedItem => selectedItem.transactionId !== item.transactionId);
      }
    },
    getDisabled(item: TransactionEtablissement): boolean {
      return !(item.type == TransactionTypes.VIREMENT_EMIS && item.deliveryId == '');
    },
    isSelected(item: TransactionEtablissement): boolean {
      return !!this.selectedItems.find(a => a.transactionId == item.transactionId);
    },
    selectAll(transactions: TransactionEtablissement[]) {
      if (this.selectedItems.length > 0) {
        this.selectedItems = [];
      }
    },
    formatDate(value: Date): string {
      return DateFormat.getDateLabel(value);
    },
    formatCurrency(value: number): string {
      return getCurrency(value);
    },
    search(): void {
      this.selectedItems = [];
      const query = new TransactionEtablissementQuery();
      query.etablissementId = this.etablissementId.key;
      this.loadTable(query);
    },
    generate(): void {
      this.generateLoading = true;
      const ids: number[] = [];
      this.selectedItems.forEach((item) => {
        ids.push(item.transactionId);
      });
      const deliverList = new TransactionList();
      deliverList.objType = ObjTypes.ETABLISSEMENT;
      deliverList.ids = ids;
      TransactionList.deliverTransactions(deliverList).then(() => {
        this.generateLoading = false;
        this.selectedItems = [];
        this.search();
      });
    },
    exportTransactions(): void {
      // to implement
    },
    setExtended(extended: Extended): void {
      this.$emit('extended', extended);
    },
    closeSection(): void {
      this.$emit('closeDialog');
    },
  },
});
</script>
